import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InputField from 'components/InputField';
import TextField from 'components/TextField';
import Card from 'components/Card';
import Button from 'components/Button';
import ErrorBox from 'components/ErrorBox';
import Backdrop from 'components/Backdrop';
import { createJobApplication, getAllJobOpenings, getDepartments, getAllApplicants, createApplicant } from 'api/apiHelper';
import { toast } from 'react-toastify';
import { JobOpeningType } from 'types/careers-types';

type DepartmentType = {
  id: number;
  name: string;
};

type ApplicantType = {
  id: string | number;
  name: string;
  email: string;
  city?: string;
  contact?: string;
};

interface ApplicantFormData {
  name: string;
  email: string;
  city: string;
  contact: string;
  notes: string;
}

interface JobApplicationFormData {
  job_opening: string;
  applicant: string;
  resume: File | null;
  notes: string;
}

const CreateJobApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Applicant form data
  const [applicantData, setApplicantData] = useState<ApplicantFormData>({
    name: '',
    email: '',
    city: '',
    contact: '',
    notes: '',
  });

  // Job application form data
  const [jobApplicationData, setJobApplicationData] = useState<JobApplicationFormData>({
    job_opening: '',
    applicant: '',
    resume: null,
    notes: '',
  });

  const [departments, setDepartments] = useState<DepartmentType[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [jobOpenings, setJobOpenings] = useState<JobOpeningType[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobOpeningType[]>([]);
  const [applicants, setApplicants] = useState<ApplicantType[]>([]);
  const [existingApplicants, setExistingApplicants] = useState<{ email: string }[]>([]);

  // Determine if applicant fields are filled
  const isApplicantDataFilled = () => {
    return !!(applicantData.name && applicantData.email && applicantData.city && applicantData.contact);
  };

  // Determine if applicant is selected from dropdown
  const isApplicantSelected = () => {
    return !!jobApplicationData.applicant;
  };

  // Check if applicant fields should be disabled
  const isApplicantFieldsDisabled = isApplicantSelected();
  
  // Check if applicant dropdown should be disabled
  const isApplicantDropdownDisabled = isApplicantDataFilled();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [departmentsRes, jobsRes, applicantsRes] = await Promise.all([
          getDepartments(),
          getAllJobOpenings(),
          getAllApplicants(),
        ]);

        const departmentsList = Array.isArray(departmentsRes.data)
          ? departmentsRes.data
          : departmentsRes.data?.results || [];
        setDepartments(departmentsList);

        const jobsList = Array.isArray(jobsRes.data)
          ? jobsRes.data
          : jobsRes.data?.results || [];
        setJobOpenings(jobsList);
        setFilteredJobs(jobsList);

        const applicantsList = Array.isArray(applicantsRes.data)
          ? applicantsRes.data
          : applicantsRes.data?.results || [];
        setApplicants(applicantsList);
        setExistingApplicants(applicantsList);
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load data.');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      const selectedDeptId = selectedDepartment.toString();
      const filtered = jobOpenings.filter(
        (job) => job.department?.id?.toString() === selectedDeptId
      );
      setFilteredJobs(filtered);
      setJobApplicationData((prev) => ({ ...prev, job_opening: '' }));
    } else {
      setFilteredJobs(jobOpenings);
      setJobApplicationData((prev) => ({ ...prev, job_opening: '' }));
    }
  }, [selectedDepartment, jobOpenings]);

  // When applicant is selected from dropdown, clear applicant form fields
  useEffect(() => {
    if (isApplicantSelected()) {
      setApplicantData({
        name: '',
        email: '',
        city: '',
        contact: '',
        notes: '',
      });
      setEmailError(null);
    }
  }, [jobApplicationData.applicant]);

  // When applicant fields are filled, clear dropdown selection
  useEffect(() => {
    if (isApplicantDataFilled()) {
      setJobApplicationData((prev) => ({ ...prev, applicant: '' }));
    }
  }, [applicantData.name, applicantData.email, applicantData.city, applicantData.contact]);

  const handleApplicantChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle phone number - only allow digits
    if (name === 'contact') {
      const numericValue = value.replace(/\D/g, '');
      setApplicantData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    // Handle email - check uniqueness
    if (name === 'email') {
      const emailValue = value.toLowerCase().trim();
      setApplicantData((prev) => ({
        ...prev,
        [name]: emailValue,
      }));
      
      // Check if email already exists
      const emailExists = existingApplicants.some(
        (app) => app.email.toLowerCase() === emailValue
      );
      if (emailExists) {
        setEmailError('This email address is already registered to another applicant.');
      } else {
        setEmailError(null);
      }
      return;
    }

    setApplicantData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplicantNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setApplicantData((prev) => ({
      ...prev,
      notes: e.target.value,
    }));
  };

  const handleJobApplicationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setJobApplicationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleJobApplicationNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJobApplicationData((prev) => ({
      ...prev,
      notes: e.target.value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setJobApplicationData((prev) => ({
        ...prev,
        resume: e.target.files![0],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setEmailError(null);

    // Validation: Either applicant fields must be filled OR applicant must be selected
    if (!isApplicantDataFilled() && !isApplicantSelected()) {
      setError('Please either fill in applicant information or select an existing applicant.');
      setLoading(false);
      return;
    }

    // Validate applicant data if filled
    if (isApplicantDataFilled()) {
      if (!applicantData.name || !applicantData.email || !applicantData.city || !applicantData.contact) {
        setError('Name, email, city, and contact are required fields.');
        setLoading(false);
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(applicantData.email)) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      // Check email uniqueness again before submitting
      const emailExists = existingApplicants.some(
        (app) => app.email.toLowerCase() === applicantData.email.toLowerCase()
      );
      if (emailExists) {
        setEmailError('This email address is already registered to another applicant.');
        setError('This email address is already registered to another applicant.');
        setLoading(false);
        return;
      }
    }

    // Validate job application data
    if (!jobApplicationData.job_opening || !jobApplicationData.resume) {
      setError('Job opening and resume are required fields.');
      setLoading(false);
      return;
    }

    try {
      let applicantName = '';
      let applicantEmail = '';

      // If applicant fields are filled, create applicant first
      if (isApplicantDataFilled()) {
        try {
          const newApplicant = await createApplicant({
            name: applicantData.name,
            email: applicantData.email,
            city: applicantData.city,
            contact: applicantData.contact,
            notes: applicantData.notes || undefined,
          });
          applicantName = applicantData.name;
          applicantEmail = applicantData.email;
          
          // Refresh applicants list
          const applicantsRes = await getAllApplicants();
          const applicantsList = Array.isArray(applicantsRes.data)
            ? applicantsRes.data
            : applicantsRes.data?.results || [];
          setApplicants(applicantsList);
          setExistingApplicants(applicantsList);
        } catch (err: any) {
          // Check for email uniqueness error from backend
          const errorData = err?.response?.data;
          let errorMessage = 'Failed to create applicant.';
          
          if (errorData?.email) {
            errorMessage = Array.isArray(errorData.email)
              ? errorData.email[0]
              : errorData.email;
            if (errorMessage.toLowerCase().includes('already exists') || 
                errorMessage.toLowerCase().includes('unique')) {
              errorMessage = 'This email address is already registered to another applicant.';
              setEmailError(errorMessage);
            }
          } else if (errorData?.error) {
            errorMessage = errorData.error;
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          } else if (err?.message) {
            errorMessage = err.message;
          }
          
          setError(errorMessage);
          toast.error(errorMessage);
          setLoading(false);
          return;
        }
      } else {
        // Get applicant details from selected applicant
        const selectedApplicant = applicants.find(
          (app) => String(app.id) === jobApplicationData.applicant
        );
        if (!selectedApplicant) {
          setError('Selected applicant not found.');
          setLoading(false);
          return;
        }
        applicantName = selectedApplicant.name;
        applicantEmail = selectedApplicant.email;
      }

      // Create job application
      const formDataToSend = new FormData();
      formDataToSend.append('job_opening', jobApplicationData.job_opening);
      formDataToSend.append('applicant_name', applicantName);
      formDataToSend.append('applicant_email', applicantEmail);
      formDataToSend.append('resume', jobApplicationData.resume);
      if (jobApplicationData.notes) {
        formDataToSend.append('notes', jobApplicationData.notes);
      }

      await createJobApplication(formDataToSend);
      toast.success('Job application created successfully!');
      navigate('/careers?tab=2'); // Navigate to Job Applications tab (index 2)
    } catch (err: any) {
      const errorData = err?.response?.data;
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || '';
      const errorString = JSON.stringify(errorData || errorMessage || err).toLowerCase();
      
      // Check for unique constraint violation (duplicate job application)
      if (
        err?.response?.status === 500 ||
        errorString.includes('unique constraint') ||
        errorString.includes('already exists') ||
        errorString.includes('job_opening_id') && errorString.includes('applicant_id')
      ) {
        // Get job opening name for better error message
        const selectedJob = filteredJobs.find(job => job.slug === jobApplicationData.job_opening);
        const jobTitle = selectedJob?.title || 'this job';
        const applicantName = isApplicantDataFilled() 
          ? applicantData.name 
          : applicants.find(app => String(app.id) === jobApplicationData.applicant)?.name || 'this applicant';
        
        const friendlyMessage = `${applicantName} has already applied for ${jobTitle}. Each applicant can only apply once per job opening.`;
        setError(friendlyMessage);
        toast.error(friendlyMessage);
      } else {
        // Handle other errors
        const finalErrorMessage = errorMessage || 'Failed to create job application.';
        setError(finalErrorMessage);
        toast.error(finalErrorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      <Card extra="p-6">
        {error && <ErrorBox errorMessage={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Applicant Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Applicant Information
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {isApplicantSelected() 
                ? 'An existing applicant has been selected. Fill in the fields below to create a new applicant instead, or select a different applicant from the dropdown in the Job Application section.'
                : 'Fill in the applicant details below, or select an existing applicant from the dropdown in the Job Application section.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                id="name"
                name="name"
                label="Full Name"
                type="text"
                placeholder="Enter applicant full name"
                extra="w-full"
                variant="outlined"
                value={applicantData.name}
                onChange={handleApplicantChange}
                required={!isApplicantSelected()}
                disabled={isApplicantFieldsDisabled}
              />
              <div>
                <InputField
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter email address"
                  extra="w-full"
                  variant="outlined"
                  value={applicantData.email}
                  onChange={handleApplicantChange}
                  required={!isApplicantSelected()}
                  disabled={isApplicantFieldsDisabled}
                  state={emailError ? 'error' : undefined}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-500 dark:text-red-400">{emailError}</p>
                )}
              </div>
              <InputField
                id="city"
                name="city"
                label="City"
                type="text"
                placeholder="Enter city"
                extra="w-full"
                variant="outlined"
                value={applicantData.city}
                onChange={handleApplicantChange}
                required={!isApplicantSelected()}
                disabled={isApplicantFieldsDisabled}
              />
              <InputField
                id="contact"
                name="contact"
                label="Contact Number"
                type="tel"
                placeholder="Enter contact number (digits only)"
                extra="w-full"
                variant="outlined"
                value={applicantData.contact}
                onChange={handleApplicantChange}
                inputMode="numeric"
                pattern="[0-9]*"
                required={!isApplicantSelected()}
                disabled={isApplicantFieldsDisabled}
              />
            </div>
            <div className="mt-4">
              <TextField
                id="applicant_notes"
                label="Applicant Notes"
                placeholder="Enter any additional notes about the applicant (optional)"
                extra="w-full"
                rows={3}
                value={applicantData.notes}
                onChange={handleApplicantNotesChange}
              />
            </div>
          </div>

          {/* Job Application Section */}
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Job Application Details
            </h3>
            
            {/* Department and Job Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-bold text-navy-700 dark:text-white mb-2">
                  Department
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Select a department...</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-navy-700 dark:text-white mb-2">
                  Job Opening
                </label>
                <select
                  name="job_opening"
                  value={jobApplicationData.job_opening}
                  onChange={handleJobApplicationChange}
                  disabled={!selectedDepartment || filteredJobs.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">
                    {!selectedDepartment
                      ? 'Select department first...'
                      : filteredJobs.length === 0
                      ? 'No jobs available in this department'
                      : 'Select a job...'}
                  </option>
                  {filteredJobs.map((job) => (
                    <option key={job.id} value={job.slug}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Applicant Selection Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-navy-700 dark:text-white mb-2">
                Or Select Existing Applicant
              </label>
              <select
                name="applicant"
                value={jobApplicationData.applicant}
                onChange={handleJobApplicationChange}
                disabled={isApplicantDropdownDisabled}
                className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {isApplicantDropdownDisabled
                    ? 'Fill in applicant fields above or clear them to select...'
                    : 'Select an existing applicant...'}
                </option>
                {applicants.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name} ({app.email})
                  </option>
                ))}
              </select>
              {isApplicantDropdownDisabled && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Applicant fields are filled. Clear them to select an existing applicant.
                </p>
              )}
            </div>

            {/* Resume Upload */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-navy-700 dark:text-white mb-2">
                Resume
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
                required
              />
              {jobApplicationData.resume && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Selected: {jobApplicationData.resume.name}
                </p>
              )}
            </div>

            {/* Job Application Notes */}
            <div>
              <TextField
                id="job_application_notes"
                label="Job Application Notes"
                placeholder="Enter any additional notes about this job application (optional)"
                extra="w-full"
                rows={4}
                value={jobApplicationData.notes}
                onChange={handleJobApplicationNotesChange}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-navy-700">
            <Button
              type="button"
              onClick={() => navigate('/careers?tab=2')}
              className="px-6 py-2 border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              Create Application
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateJobApplication;
